import { usePrintCustomSection, type CustomRow, type CustomSectionOptions } from "~/composables/usePrintCustomSection";
import type { ReceiptItemCustomSection } from "~/interface/ReceiptOrder";

/**
 * Topping / comment lines for a printed order line.
 *
 * One line per topping and one line per comment, instead of the single
 * comma-joined string the printer has always received (`toppingList`,
 * `comments`). The lines are carried in the item's `Custom` block — the same
 * `Rows/Underline/Bold/Italic/Indent` section the customer receipt already uses
 * for the per-line discount (ReceiptOrder.ts `buildItemDiscountCustom`), which
 * is why `usePrintCustomSection` was written print-surface agnostic.
 *
 * ONLY the kitchen slip splits into lines, and only on a printer new enough to
 * render an item `Custom` block (see `supportsItemCustomText`). The customer
 * receipt keeps the comma-joined string — it just gains the `xN` suffix.
 */

/** Leading spaces so a kitchen detail row sits under the item NAME, past the qty column. */
export const KITCHEN_CUSTOM_INDENT = 8;

/**
 * `ไข่ดาว` for a single topping, `ไข่ดาว x2` from two up. The suffix is the
 * topping's own quantity — NOT the line quantity — so a line of 3 noodles with
 * 2 eggs each still reads `x2`, exactly as `joinToppingText` has always printed
 * it inside the joined string.
 */
export function formatToppingLabel(topping: any): string {
  const name = String(topping?.name ?? "").trim();
  const qty = Number(topping?.quantity ?? 0);
  if (!name) return "";
  return qty > 1 ? `${name} x${qty}` : name;
}

/** The legacy one-string form: `ไข่ดาว x2, ไข่เจียว x2, ไข่`. */
export function joinToppingLabels(toppings: any): string {
  if (!Array.isArray(toppings)) return "";
  return toppings.map(formatToppingLabel).filter(Boolean).join(", ");
}

/**
 * The comment field is stored as one comma-joined string (ToppingComment.vue
 * joins the picked presets with ", "), so the split mirrors how it was built.
 * A comment the staff typed with a comma inside it splits too — that is the
 * only separator the stored value has.
 */
export function splitCommentLines(comments: any): string[] {
  if (typeof comments !== "string") return [];
  return comments
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Toppings first, then comments — the order the cart shows them — and inside
 * each group the source order is kept untouched (topping array order, comment
 * split order). Never reordered, never de-duplicated.
 *
 * `value` is empty on every row: a kitchen slip prints no money.
 */
export function buildItemDetailRows(item: any): CustomRow[] {
  const toppingRows = (Array.isArray(item?.toppings) ? item.toppings : [])
    .map(formatToppingLabel)
    .filter(Boolean)
    .map((name: string) => ({ name : "- " + name, value: "" }));

  const commentRows = splitCommentLines(item?.comments).map((name) => ({ name: "- " + name, value: "" }));

  return [...toppingRows, ...commentRows];
}

/**
 * The item's `Custom` block, or undefined when the line has neither toppings
 * nor comments — an absent key is what every other Custom builder emits for
 * "nothing to add", and the print service defaults it away.
 */
export function buildItemCustomSection(
  item: any,
  options: CustomSectionOptions = {}
): ReceiptItemCustomSection | undefined {
  const rows = buildItemDetailRows(item);
  if (rows.length === 0) return undefined;

  const { buildCustomSection } = usePrintCustomSection();
  return buildCustomSection(rows, { Indent: KITCHEN_CUSTOM_INDENT, ...options });
}
